import React from 'react';
import styled from 'styled-components';
import { formatMessage } from '@99designs/frontend-components/i18n';
import { typography, colors } from '../../lib/styles';
import AnimatedStarIcon from './AnimatedStarIcon';
import Star2 from '../Icon/components/Star2';

interface Props {
    rating: number;
    scale?: 1 | 2 | 3 | 4;
    color?: string;
    chameleon?: boolean;
    animateOnScrollIntoView?: boolean;
}

const StyledStars = styled.div(({ width }: React.CSSProperties) => {
    return `
        display: flex;
        justify-content: space-between;
        line-height: 0;
        overflow: hidden;
        width: ${width};
    `;
});

const Star = styled.div(({ maxWidth }: React.CSSProperties) => {
    return `
        position: relative;
        overflow: hidden;
        max-width: ${maxWidth};
        max-height: ${maxWidth};
    `;
});

const StarOverlay = styled.div(({ width }: React.CSSProperties) => {
    return `
        position: absolute;
        top: 0;
        left: -${width ? width : 0}%;
        width: 100%;
        height: 100%;
        overflow: hidden;

        > svg {
            position: absolute;
            top: 0;
            left: ${width ? width : 0}%;
        }
    `;
});

const StarIcon = (props: React.SVGProps<SVGSVGElement>) => (
    <Star2 {...props} width="100%" height="100%" />
);

export default function Stars({
    rating,
    chameleon = false,
    scale,
    color = colors.heritage,
    animateOnScrollIntoView = false,
}: Props) {
    const sizeMap = { 1: '107px', 2: '160px', 3: '220px', 4: '300px' };
    const starMaxWidths = [
        `${typography.typeScale.size4}px`,
        `${typography.typeScale.size5}px`,
        `${typography.typeScale.size6}px`,
        `${typography.typeScale.size8}px`,
    ];

    return (
        <StyledStars
            width={scale ? sizeMap[scale] : 'auto'}
            aria-label={formatMessage(__('Rating: {rating} out of 5 stars'), {
                rating,
            })}
            role="img"
        >
            {[1, 2, 3, 4, 5].map((starIndex: number) => {
                if (animateOnScrollIntoView) {
                    return (
                        <Star maxWidth={scale ? starMaxWidths[scale - 1] : '16%'} key={starIndex}>
                            <AnimatedStarIcon
                                baseColor={colors.grey500}
                                fillColor={chameleon ? 'currentColor' : color}
                                starIndex={starIndex}
                            />
                        </Star>
                    );
                }

                let overlayWidth;
                if (starIndex <= rating) {
                    overlayWidth = 100;
                } else if (starIndex - rating > 1) {
                    overlayWidth = 0;
                } else {
                    overlayWidth = (1 - (starIndex - rating)) * 100;
                }

                return (
                    <Star maxWidth={scale ? starMaxWidths[scale - 1] : '16%'} key={starIndex}>
                        <StarIcon color={colors.grey500} />

                        <StarOverlay width={100 - overlayWidth}>
                            <StarIcon color={chameleon ? 'currentColor' : color} />
                        </StarOverlay>
                    </Star>
                );
            })}
        </StyledStars>
    );
}
